import { useEffect, useState, useRef } from "react";
import { ChevronsUpDown, Check, Plus, Briefcase } from "lucide-react";
import { useWorkspacesStore } from "@/stores/workspaces";
import { cn } from "@/lib/utils";

interface WorkspaceSwitcherProps {
  onSwitch: () => void;
  onCreateNew: () => void;
}

export function WorkspaceSwitcher({ onSwitch, onCreateNew }: WorkspaceSwitcherProps) {
  const { workspaces, current, currentId, fetch, switchTo } = useWorkspacesStore();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetch(); }, []);

  // Click-outside handler
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSelect = (id: number) => {
    if (id !== currentId) {
      switchTo(id);
      onSwitch();
    }
    setOpen(false);
  };

  const handleNew = () => {
    setOpen(false);
    onCreateNew();
  };

  return (
    <div className="relative px-3" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-left transition-colors",
          "bg-white/5 hover:bg-white/10 text-white"
        )}
      >
        <div className="w-7 h-7 rounded bg-brand-600 flex items-center justify-center shrink-0">
          <Briefcase className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold tracking-[0.08em] uppercase text-brand-400">
            Workspace
          </p>
          <p className="text-[13px] font-semibold text-white truncate">
            {current?.name || "Loading..."}
          </p>
        </div>
        <ChevronsUpDown className="w-3.5 h-3.5 text-brand-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-white rounded-md border border-slate-200 shadow-lg py-1 max-h-80 overflow-y-auto">
          {workspaces.map(ws => (
            <button
              key={ws.id}
              onClick={() => handleSelect(ws.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-brand-950 truncate">{ws.name}</p>
                {ws.description && (
                  <p className="text-[11px] text-slate-400 truncate">{ws.description}</p>
                )}
              </div>
              {ws.id === currentId && <Check className="w-4 h-4 text-brand-600 shrink-0" />}
            </button>
          ))}
          <div className="border-t border-slate-100 mt-1 pt-1">
            <button
              onClick={handleNew}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors text-brand-600"
            >
              <Plus className="w-4 h-4 shrink-0" />
              <span className="text-[13px] font-medium">New Workspace</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
